import { Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import { SyntheticEvent } from "react";
import UserRoles, { Role } from "../../../../constants/roles";
import useAuth from "../../../../context/UserContext";

import { CheckboxWithLabel } from "../../../../components/ui/CheckboxWithLabel";
import { getAllowedRoles } from "../../../../lib/Util";
import { RoleDisclaimer } from "./RoleDisclaimer";

type RoleSelectionProps = {
  selectedRoles: string[];
  globalRoles: string[];
  onClose: () => void;
  anchorEl: HTMLElement | null;
  handleCheckboxClick: (e: SyntheticEvent, checked: boolean) => void;
};

const RoleSelection = ({ selectedRoles, globalRoles, onClose, anchorEl, handleCheckboxClick }: RoleSelectionProps) => {
  const { user } = useAuth();
  const allowedRoles = user.roles ? getAllowedRoles(user.roles)?.map((role) => role) : [];

  const availableRoles = Object.values(UserRoles).filter(({ value }) => allowedRoles.includes(value));

  const disableCheckbox = (value: string) => selectedRoles.includes(value) && selectedRoles.length === 1;

  return (
    <Menu
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      slotProps={{
        paper: {
          style: {
            borderRadius: "4px",
            padding: "24px",
          },
        },
      }}
    >
      <Stack sx={{ gap: "12px" }}>
        {availableRoles.map(({ value, label }) => {
          const checkbox = (
            <CheckboxWithLabel
              value={value}
              checked={selectedRoles.includes(value)}
              onChange={handleCheckboxClick}
              label={label}
              disabled={disableCheckbox(value)}
              checkboxSx={{ marginRight: "8px" }}
              sx={{ marginRight: "0px" }}
            />
          );

          const checkboxWithTooltip = (
            <Tooltip
              title="You can’t remove the last role of a user (no matter if it’s a specific or global role)."
              arrow
              placement="right"
              slotProps={{
                tooltip: {
                  sx: { maxWidth: "280px" },
                },
              }}
              describeChild
            >
              {checkbox}
            </Tooltip>
          );

          return (
            <Stack key={value} direction="row">
              <MenuItem
                key={value}
                value={value}
                sx={(scopedTheme) => ({
                  "&.Mui-selected": {
                    backgroundColor: scopedTheme.palette.surfaceLight10.main,
                  },
                  "&.MuiMenuItem-root": {
                    padding: "0",
                  },
                })}
              >
                {disableCheckbox(value) ? checkboxWithTooltip : checkbox}
              </MenuItem>
              {globalRoles.includes(value as Role) ? <RoleDisclaimer roleLabel={label} /> : null}
            </Stack>
          );
        })}
      </Stack>
    </Menu>
  );
};

export { RoleSelection };
